import React, { useState, useRef, useEffect } from "react";
import { Star, ChevronRight, Award, Sparkles } from "lucide-react";
import classes from "./spotlightArtists.module.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchSpotlightArtists } from "../../redux/slices/artistSlice";
import { ARTIST_2, ARTIST_3 } from "../../utils/images";
import { useNavigate } from "react-router-dom";

const SpotlightArtists = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get spotlight artists from Redux store
  const { spotlightArtists, spotlightArtistsLoading } = useSelector(
    (state) => state.artists
  );

  // Sample placeholder data (structure matches the JSON you provided)
  const placeholderData = {
    first_name: "Loading",
    last_name: "Artist",
    gender: "neutral",
    location: "...",
    experience_years: 0,
    rating: 0,
    profile_picture: "",
  };

  // Transform the artist data to the format needed for the component
  const transformedArtists = spotlightArtists?.map((artist, index) => {
    // Generate a placeholder image URL - in production, use artist.profile_picture
    const imageUrl = artist.profile_picture || ARTIST_3;

    return {
      id: index + 1,
      username: artist?.username,
      name:
        artist.first_name != undefined
          ? `${artist.first_name} ${artist.last_name}`
          : "Artist Name",
      // Derive specialty based on experience years
      specialty:
        getSpecialtyFromExperience(artist.experience_years) || "Singer",
      location: artist.location || "Noida",
      image: imageUrl || ARTIST_3,
      profileImage: imageUrl || ARTIST_3,
      stats: {
        // Generate plausible follower count based on experience
        followers: artist?.experience_years
          ? `${Math.floor(artist.experience_years * 2 + Math.random() * 10)}K`
          : "100k",
        // Generate plausible sales based on experience and rating

        rating: artist.rating || 4.9,
      },
      // Generate a description based on specialty
      description: getDescriptionFromSpecialty(
        getSpecialtyFromExperience(artist.experience_years)
      ),
      experience_years: artist?.experience_years?.max,
      // Generate a featured work title
      featuredWork:
        getFeaturedWorkTitle(
          artist.first_name,
          getSpecialtyFromExperience(artist.experience_years)
        ) || "Performance at Taj Hotel",
    };
  });

  const handlePortfolioClick = (username) => {
    navigate(`/profile/${username}`);
  };

  // Helper functions to derive additional artist information
  function getSpecialtyFromExperience(years) {
    const specialties = [
      "Contemporary Realism",
      "Abstract Expressionism",
      "Digital Art",
      "Surrealism",
      "Impressionism",
      "Minimalism",
      "Pop Art",
      "Neo-Classical",
    ];

    // Deterministic but varied selection based on years of experience
    return specialties[Math.floor(years % specialties?.length)];
  }

  function getDescriptionFromSpecialty(specialty) {
    const descriptions = {
      "Contemporary Realism":
        "Capturing the essence of modern life through precise observation and technical mastery. My work reveals beauty in everyday moments.",
      "Abstract Expressionism":
        "Exploring emotional depth through spontaneous, gestural brushwork. My pieces invite viewers to experience raw emotion through color and form.",
      "Digital Art":
        "Pushing the boundaries of technology and tradition. My digital creations blend innovative techniques with timeless artistic principles.",
      Surrealism:
        "Creating dreamscapes that blend reality with fantasy. My work explores the subconscious mind through vibrant imagery.",
      Impressionism:
        "Capturing fleeting moments through light and color. My impressionist works celebrate the ephemeral beauty of the world around us.",
      Minimalism:
        "Finding power in simplicity. My minimalist approach strips away the unnecessary to reveal fundamental truths through clean lines and spaces.",
      "Pop Art":
        "Celebrating and critiquing popular culture through bold colors and familiar imagery. My work bridges the gap between commercial and fine art.",
      "Neo-Classical":
        "Reimagining classical traditions for the modern era. My pieces honor artistic heritage while speaking to contemporary concerns.",
    };

    return (
      descriptions[specialty] ||
      "Exploring diverse artistic expressions through a unique perspective that bridges tradition and innovation."
    );
  }

  function getFeaturedWorkTitle(firstName, specialty) {
    const titlesBySpecialty = {
      "Contemporary Realism": [
        "Urban Solitude",
        "Morning Light",
        "Passage of Time",
      ],
      "Abstract Expressionism": [
        "Emotional Cascade",
        "Inner Landscape",
        "Chromatic Journey",
      ],
      "Digital Art": [
        "Virtual Consciousness",
        "Digital Dreams",
        "Binary Beauty",
      ],
      Surrealism: [
        "The Dreamer's Gateway",
        "Impossible Horizons",
        "Subconscious Echoes",
      ],
      Impressionism: [
        "Fleeting Moments",
        "Garden at Dawn",
        "Riverside Reflections",
      ],
      Minimalism: ["Essential Forms", "Negative Space", "Silent Harmony"],
      "Pop Art": ["Consumer Icons", "Media Mosaic", "Cultural Artifacts"],
      "Neo-Classical": [
        "Modern Mythology",
        "Heritage Reimagined",
        "Timeless Figures",
      ],
    };

    // Get titles for the specialty
    const titles = titlesBySpecialty[specialty] || [
      "Untitled Masterpiece",
      "Creative Exploration",
      "Artistic Vision",
    ];

    // Deterministic selection based on the first letter of the name
    const index = firstName?.charCodeAt(0) % titles?.length;
    return titles[index];
  }

  // Scroll to active artist
  useEffect(() => {
    if (scrollRef.current && transformedArtists?.length > 0) {
      const scrollAmount =
        activeIndex *
        (scrollRef.current.offsetWidth / transformedArtists?.length);
      scrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  }, [activeIndex, transformedArtists?.length]);

  // Fetch spotlight artists on component mount
  useEffect(() => {
    dispatch(fetchSpotlightArtists());
  }, [dispatch]);

  // Automatic rotation
  useEffect(() => {
    if (transformedArtists?.length === 0) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % transformedArtists?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [transformedArtists?.length]);

  const handleArtistClick = (index) => {
    setActiveIndex(index);
  };

  // Show  spotlightArtistsLoading state if no artists are available yet
  if (spotlightArtistsLoading || transformedArtists?.length === 0) {
    return (
      <section className={classes.spotlightSection}>
        <div className={classes.spotlightHeader}>
          <div className={classes.headerContent}>
            <div className={classes.titleWrapper}>
              <Sparkles className={classes.sparkleIcon} size={24} />
              <h2 className={classes.sectionTitle}>Artist Spotlight</h2>
            </div>
            <p className={classes.sectionDescription}>
              Loading featured artists...
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={classes.spotlightSection}>
      <div className={classes.spotlightHeader}>
        <div className={classes.headerContent}>
          <div className={classes.titleWrapper}>
            <Sparkles className={classes.sparkleIcon} size={24} />
            <h2 className={classes.sectionTitle}>Artist Spotlight</h2>
          </div>
          <p className={classes.sectionDescription}>
            Exclusive showcase of our premier artists
          </p>
        </div>
        <div className={classes.headerCounter}>
          <span className={classes.activeCounter}>
            {String(activeIndex + 1).padStart(2, "0")}
          </span>
          <span className={classes.counterDivider}>/</span>
          <span className={classes.totalCounter}>
            {String(transformedArtists?.length).padStart(2, "0")}
          </span>
        </div>
      </div>

      <div className={classes.spotlightContent}>
        <div className={classes.spotlightMain}>
          {transformedArtists?.map((artist, index) => (
            <div
              key={artist.id}
              className={`${classes.spotlightCard} ${
                index === activeIndex ? classes.activeCard : ""
              }`}
            >
              <div className={classes.spotlightImageWrapper}>
                <img
                  src={artist?.image || ARTIST_3}
                  alt={artist?.name || "image"}
                  className={classes.spotlightImage}
                />
                <div className={classes.imageOverlay}></div>
              </div>
              <div className={classes.spotlightInfo}>
                <div className={classes.artistHeader}>
                  <img
                    src={artist?.profileImage}
                    alt={artist ? `${artist?.name} profile` : "artist image"}
                    className={classes.artistProfileImage}
                  />
                  <div className={classes.artistBadge}>
                    <Award size={14} className={classes.badgeIcon} />
                    <span>Featured Artist</span>
                  </div>
                </div>

                <h3 className={classes.artistName}>{artist.name}</h3>
                <p className={classes.artistSpecialty}>{artist.specialty}</p>
                <p className={classes.artistLocation}>{artist.location}</p>

                <div className={classes.statsContainer}>
                  <div className={classes.statItem}>
                    <span className={classes.statValue}>
                      {artist?.experience_years != undefined
                        ? artist?.experience_years
                        : "5"}
                    </span>
                    <span className={classes.statLabel}>
                      Years Of Experience
                    </span>
                  </div>

                  <div className={classes.statItem}>
                    <div className={classes.ratingWrapper}>
                      <span className={classes.statValue}>
                        {artist.stats.rating}
                      </span>
                      <Star
                        size={14}
                        className={classes.ratingIcon}
                        fill="currentColor"
                      />
                    </div>
                    <span className={classes.statLabel}>Rating</span>
                  </div>
                </div>

                <p className={classes.artistDescription}>
                  {artist.description}
                </p>
                <div className={classes.featuredContainer}>
                  <span className={classes.featuredLabel}>Featured Work:</span>
                  <span className={classes.featuredTitle}>
                    "{artist.featuredWork}"
                  </span>
                </div>

                <div className={classes.actionButtons}>
                  <button
                    className={classes.primaryButton}
                    onClick={() => handlePortfolioClick(artist?.username)}
                  >
                    View Portfolio
                  </button>
                  <button className={classes.secondaryButton} disabled={true}>
                    Book Now Coming Soon
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={classes.spotlightNav} ref={scrollRef}>
          {transformedArtists?.map((artist, index) => (
            <div
              key={artist.id}
              className={`${classes.navItem} ${
                index === activeIndex ? classes.activeNavItem : ""
              }`}
              onClick={() => handleArtistClick(index)}
            >
              <div className={classes.navImageContainer}>
                <img
                  src={artist.image || ARTIST_2}
                  alt={artist.name}
                  className={classes.navImage}
                />
                <div className={classes.navOverlay}></div>
              </div>
              <div className={classes.navInfo}>
                <h4 className={classes.navName}>
                  {artist.name || "Artist name"}
                </h4>
                <p className={classes.navSpecialty}>{artist.specialty}</p>
              </div>
              <ChevronRight size={16} className={classes.navArrow} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpotlightArtists;
